/**
 * Admin Accessibility Menu Component
 *
 * Quick-access menu for toggling accessibility settings during admin testing.
 * Settings are stored in sessionStorage (not user preferences DB).
 */

import { useState, useEffect } from 'react'
import { Eye, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  getAdminAccessibilitySettings,
  saveAdminAccessibilitySettings,
  applyAdminAccessibilitySettings,
  resetAdminAccessibilitySettings,
  type AdminAccessibilitySettings,
} from '@admin/lib/admin-accessibility'

export function AccessibilityMenu() {
  const [settings, setSettings] = useState<AdminAccessibilitySettings>(() =>
    getAdminAccessibilitySettings(),
  )
  const [isOpen, setIsOpen] = useState(false)

  // Update DOM when settings change
  useEffect(() => {
    applyAdminAccessibilitySettings(settings)
    saveAdminAccessibilitySettings(settings)
  }, [settings])

  const handleReset = () => {
    resetAdminAccessibilitySettings()
    setSettings(getAdminAccessibilitySettings())
  }

  const updateSetting = <K extends keyof AdminAccessibilitySettings>(
    key: K,
    value: AdminAccessibilitySettings[K],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-muted"
          title="Admin Accessibility Testing (local preview)"
        >
          <Eye className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" side="top">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">Accessibility Testing</h4>
              <p className="text-xs text-muted-foreground">Preview mode (session only)</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleReset} title="Reset to defaults">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          <div className="border-t pt-3 space-y-4">
            {/* Font Size */}
            <div className="space-y-2">
              <Label htmlFor="a11y-font-size" className="text-xs font-medium">
                Font Size
              </Label>
              <Select
                value={settings.fontSize}
                onValueChange={(value) =>
                  updateSetting('fontSize', value as AdminAccessibilitySettings['fontSize'])
                }
              >
                <SelectTrigger id="a11y-font-size" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small (87.5%)</SelectItem>
                  <SelectItem value="medium">Default (100%)</SelectItem>
                  <SelectItem value="large">Large (112.5%)</SelectItem>
                  <SelectItem value="x-large">Extra Large (125%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Line Spacing */}
            <div className="space-y-2">
              <Label htmlFor="a11y-line-spacing" className="text-xs font-medium">
                Line Spacing
              </Label>
              <Select
                value={settings.lineSpacing}
                onValueChange={(value) =>
                  updateSetting('lineSpacing', value as AdminAccessibilitySettings['lineSpacing'])
                }
              >
                <SelectTrigger id="a11y-line-spacing" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal (1.5)</SelectItem>
                  <SelectItem value="comfortable">Comfortable (1.8)</SelectItem>
                  <SelectItem value="wide">Wide (2.0)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Toggle Switches */}
            <div className="space-y-3 border-t pt-3">
              {/* High Contrast */}
              <div className="flex items-center justify-between">
                <Label htmlFor="a11y-contrast" className="text-xs font-medium cursor-pointer">
                  High Contrast
                </Label>
                <Switch
                  id="a11y-contrast"
                  checked={settings.highContrast}
                  onCheckedChange={(checked) => updateSetting('highContrast', checked)}
                />
              </div>

              {/* Reduce Motion */}
              <div className="flex items-center justify-between">
                <Label htmlFor="a11y-motion" className="text-xs font-medium cursor-pointer">
                  Reduce Motion
                </Label>
                <Switch
                  id="a11y-motion"
                  checked={settings.reduceMotion}
                  onCheckedChange={(checked) => updateSetting('reduceMotion', checked)}
                />
              </div>

              {/* Dyslexia Font */}
              <div className="flex items-center justify-between">
                <Label htmlFor="a11y-dyslexia" className="text-xs font-medium cursor-pointer">
                  Dyslexia Font
                </Label>
                <Switch
                  id="a11y-dyslexia"
                  checked={settings.dyslexiaFont}
                  onCheckedChange={(checked) => updateSetting('dyslexiaFont', checked)}
                />
              </div>

              {/* Screen Reader Optimized */}
              <div className="flex items-center justify-between">
                <Label htmlFor="a11y-screen-reader" className="text-xs font-medium cursor-pointer">
                  Screen Reader Mode
                </Label>
                <Switch
                  id="a11y-screen-reader"
                  checked={settings.screenReaderOptimized}
                  onCheckedChange={(checked) => updateSetting('screenReaderOptimized', checked)}
                />
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="text-xs text-muted-foreground border-t pt-2">
            💡 Changes apply immediately for testing. Not saved to user preferences.
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
